
import { supabase } from '@/lib/supabase';
import { PremiumBot, BotPremiumDetails } from '@/types/premium-bots';
import { Bot } from '@/types/trader';

// Fetch all premium bots with their details
export const fetchPremiumBots = async (userId: string): Promise<PremiumBot[]> => {
  try {
    console.log('Fetching premium bots for user:', userId);
    
    // First, fetch all bots with type 'premium_bot'
    const { data: bots, error: botsError } = await supabase
      .from('bots')
      .select('*')
      .eq('type', 'premium_bot');
    
    if (botsError) {
      console.error('Error fetching premium bots:', botsError);
      throw botsError;
    }
    
    if (!bots || bots.length === 0) {
      console.log('No premium bots found');
      return [];
    }
    
    console.log(`Found ${bots.length} premium bots`);
    
    // Get all premium bot details
    const { data: premiumDetails, error: premiumError } = await supabase
      .from('bot_premium_details')
      .select('*');
    
    if (premiumError) {
      console.error('Error fetching premium details:', premiumError);
      throw premiumError;
    }
    
    // Get all bot_accounts for the current user to check integration status
    // IMPORTANT: Check both bot_id and original_bot_id to handle reverse position connections
    let userBotAccounts = [];
    if (userId) {
      const botIds = bots.map(bot => bot.id);
      
      const { data: accounts, error: accountsError } = await supabase
        .from('bot_accounts')
        .select('bot_id, original_bot_id, status')
        .eq('user_id', userId)
        .or(`bot_id.in.(${botIds.join(',')}),original_bot_id.in.(${botIds.join(',')})`);
      
      if (accountsError) {
        console.error('Error fetching user bot accounts:', accountsError);
        // We'll continue with empty accounts array, but log the error
      } else {
        userBotAccounts = accounts || [];
        console.log(`Found ${userBotAccounts.length} bot accounts for user ${userId}`);
      }
    }
    
    // Map bot premium details to their respective bots
    const premiumBots: PremiumBot[] = bots.map((bot: Bot) => {
      try {
        // Find premium details for this bot
        const details = premiumDetails?.find(
          (detail: BotPremiumDetails) => detail.bot_id === bot.id
        );
        
        // Check if user has integrated this bot - look for records where either bot_id or original_bot_id matches
        let isIntegrated = false;
        
        if (userBotAccounts && userBotAccounts.length > 0) {
          isIntegrated = userBotAccounts.some(
            (account) => account.bot_id === bot.id || account.original_bot_id === bot.id
          );
          
          console.log(`Bot ${bot.id} integration status:`, isIntegrated ? 'Integrated' : 'Not integrated');
        }
        
        // Combine bot data with premium details
        return {
          ...bot,
          premiumDetails: details 
            ? { 
                ...details, 
                is_integrated: isIntegrated 
              } 
            : undefined
        };
      } catch (error) {
        console.error(`Error processing bot ${bot.id}:`, error);
        // Return bot without premium details on processing error
        return bot;
      }
    });
    
    return premiumBots;
  } catch (error) {
    console.error('Error in fetchPremiumBots:', error);
    throw error;
  }
};

// Fetch a single premium bot with all its details
export const fetchPremiumBotById = async (botId: string, userId: string): Promise<PremiumBot | null> => {
  try {
    console.log(`Fetching premium bot ${botId} for user ${userId}`);
    
    // Fetch the bot with the specified ID
    const { data: bot, error: botError } = await supabase
      .from('bots')
      .select('*')
      .eq('id', botId)
      .eq('type', 'premium_bot')
      .single();
    
    if (botError) {
      console.error('Error fetching premium bot:', botError);
      throw botError;
    }
    
    if (!bot) {
      console.log(`No bot found with ID ${botId}`);
      return null;
    }
    
    // Get premium details for this bot
    const { data: premiumDetail, error: detailError } = await supabase
      .from('bot_premium_details')
      .select('*')
      .eq('bot_id', botId)
      .single();
    
    if (detailError) {
      console.error('Error fetching premium bot details:', detailError);
      throw detailError;
    }
    
    // Check if the user has already integrated this bot
    // IMPORTANT: Check both bot_id and original_bot_id to handle reverse position connections
    let isIntegrated = false;
    
    if (userId) {
      try {
        // Check for accounts where either bot_id or original_bot_id matches the current bot
        const { data: botAccounts, error: accountError } = await supabase
          .from('bot_accounts')
          .select('id, status, bot_id, original_bot_id')
          .eq('user_id', userId)
          .or(`bot_id.eq.${botId},original_bot_id.eq.${botId}`);
        
        isIntegrated = !accountError && botAccounts && botAccounts.length > 0;
        console.log(`Bot ${botId} integration status for user ${userId}:`, isIntegrated ? 'Integrated' : 'Not integrated', botAccounts);
      } catch (error) {
        console.error('Error checking bot integration status:', error);
        // Continue with isIntegrated = false
      }
    }
    
    // Combine bot data with premium details
    const premiumBot: PremiumBot = {
      ...bot,
      premiumDetails: premiumDetail 
        ? { ...premiumDetail, is_integrated: isIntegrated } 
        : undefined
    };
    
    return premiumBot;
  } catch (error) {
    console.error('Error in fetchPremiumBotById:', error);
    throw error;
  }
};
